import { Fragment, useEffect, useRef, useState } from 'react'

import {
  MEASURES,
  NUTRIENT_COLORS,
  NUTRIENT_LABELS,
  TARGETED,
  amountForNutrient,
  unitsOf,
  nutrientsFor,
  takesAWeight,
  type Amount,
  type Food,
  type GoalOnDay,
  type Nutrients,
} from '~/data/nutrition'
import { useAuth } from '~/auth/auth-context'
import { FoodDials } from '~/components/food-dials'

/**
 * What the digits are going into: the amount itself, or one of the figures it comes to.
 * Calories first, then the macros in the order a day's head reads them.
 */
const FIGURES = ['amount', ...TARGETED] as const

type Figure = (typeof FIGURES)[number]

/**
 * The amount a food is logged in, and what that comes to: a number, the units it can be taken
 * in — the food's own weight or volume, and each of its portions — and, as it is typed, the
 * calories and macros it comes to, what the day would have left of each target once it is
 * logged, and what share of each target it is worth.
 *
 * The number being typed can be the amount or any of the calories and the macros, and the rest
 * follow from it: typing the calories wanted arrives at the amount that comes to them. On
 * Android that choice is made on a pad of the app's own; here the system keyboard stays, and
 * the choice is a row of chips beside the field saying what the digits are answering.
 *
 * A recipe's ingredient takes the same step without the day around it.
 */
export function AmountStep({
  food,
  amount,
  onAmount,
  time,
  onTime,
  goal = null,
  dayTotal = null,
  focused = true,
}: {
  food: Food
  amount: Amount
  onAmount: (next: Amount) => void
  /** The time of day, where the step is about an entry rather than an ingredient. */
  time?: string | undefined
  onTime?: ((next: string) => void) | undefined
  /** The goal the day is read against, for what it would have left. */
  goal?: GoalOnDay | null
  /** What the rest of the day already came to, with this entry left out of it. */
  dayTotal?: Nutrients | null
  focused?: boolean
}) {
  const { units: accountUnits } = useAuth()
  const field = useRef<HTMLInputElement>(null)
  /** Which figure the digits are going into, and what has been typed into it. */
  const [figure, setFigure] = useState<Figure>('amount')
  const [typed, setTyped] = useState<string | null>(null)

  useEffect(() => {
    if (focused) {
      field.current?.focus()
      field.current?.select()
    }
  }, [focused, food])

  /*
   * Every unit of the food's own measure — grams, ounces and pounds where it is stated by
   * weight, millilitres, spoons, cups and fluid ounces where it is stated by volume. The
   * account's weight units put its own first without keeping the others off the row.
   */
  const units: { key: string; label: string; group: string; amount: Amount }[] = [
    ...(takesAWeight(food)
      ? unitsOf(food, accountUnits).map((unit) => ({
          key: unit,
          label: unit,
          group: MEASURES[unit].measure,
          amount: { value: amount.value, unit, portion: null },
        }))
      : []),
    /*
     * A portion wears what it weighs beside its name — "serving (45 g)". The name says which
     * one it is and the weight says how much that is, and choosing between them wants both.
     * Not on a food that weighs nothing to speak of: a recipe counted in portions whose weight
     * is not known carries a serving of one gram that stands for nothing, and saying it would
     * be inventing a weight rather than reporting one.
     */
    ...food.portions.map((portion) => ({
      key: `portion:${portion.name}`,
      label: takesAWeight(food)
        ? `${portion.name} (${String(Math.round(portion.amount * 100) / 100)} ${portion.unit})`
        : portion.name,
      group: 'portion',
      amount: { value: amount.value, unit: 'portion' as const, portion: portion.name },
    })),
  ]
  const chosenKey = amount.unit === 'portion' ? `portion:${amount.portion ?? ''}` : amount.unit
  const comesTo = nutrientsFor(food, amount)

  /*
   * A figure typed for a nutrient the food does not hold arrives at no amount: no amount of
   * nothing comes to anything. It is said out loud rather than leaving the amount stuck at
   * whatever it happened to be.
   */
  const wanted = typed === null ? null : Number(typed)
  const unsolvable =
    figure !== 'amount' &&
    typed !== null &&
    typed.trim() !== '' &&
    wanted !== null &&
    Number.isFinite(wanted) &&
    amountForNutrient(food, figure, wanted, amount.unit, amount.portion) === null

  /** What the field is holding: what was typed into this figure, or the figure as it stands. */
  const shown = (() => {
    if (typed !== null) {
      return typed
    }
    if (figure === 'amount') {
      return amount.value === 0 ? '' : String(amount.value)
    }
    const held = comesTo?.[figure]
    return held === null || held === undefined ? '' : String(Math.round(held * 100) / 100)
  })()

  const take = (text: string) => {
    setTyped(text)
    const read = Number(text)
    const blank = text.trim() === '' || !Number.isFinite(read)
    if (figure === 'amount') {
      onAmount({ ...amount, value: blank ? 0 : read })
      return
    }
    if (blank) {
      onAmount({ ...amount, value: 0 })
      return
    }
    const solved = amountForNutrient(food, figure, read, amount.unit, amount.portion)
    if (solved !== null) {
      onAmount({ ...amount, value: solved })
    }
  }

  /** Choosing a different figure, or a different unit, leaves the digits behind. */
  const choose = (next: Figure) => {
    setFigure(next)
    setTyped(null)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <label className="block min-w-0 flex-1">
          <span className="mb-1 block text-sm text-ink-muted">
            {figure === 'amount' ? 'Amount' : NUTRIENT_LABELS[figure]}
          </span>
          <span className="relative block">
            <input
              ref={field}
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              aria-label={figure === 'amount' ? 'Amount' : NUTRIENT_LABELS[figure]}
              value={shown}
              onChange={(event) => {
                take(event.target.value)
              }}
              className="w-full rounded-2xl bg-surface p-3 text-3xl outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-accent"
            />
          </span>
        </label>
        {time !== undefined && onTime !== undefined && (
          <label className="block">
            <span className="mb-1 block text-sm text-ink-muted">Time</span>
            <input
              type="time"
              aria-label="Time"
              value={time}
              onChange={(event) => {
                onTime(event.target.value)
              }}
              className="rounded-2xl bg-surface p-3 text-lg outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-accent"
            />
          </label>
        )}
      </div>

      {/*
        What the digits are answering. The amount leads, because it is what a food is usually
        logged in; the rest are there for the times somebody knows the figure they want rather
        than the weight that comes to it.
      */}
      <div role="radiogroup" aria-label="What is being typed" className="flex flex-wrap gap-2">
        {FIGURES.map((each) => (
          <button
            key={each}
            type="button"
            role="radio"
            aria-checked={each === figure}
            aria-label={
              each === 'amount'
                ? 'Type the amount'
                : `Type the ${NUTRIENT_LABELS[each].toLowerCase()}`
            }
            onClick={() => {
              choose(each)
            }}
            className={`flex h-8 items-center rounded-full px-3.5 text-sm transition-colors ${
              each === figure
                ? 'bg-accent/15 text-accent ring-1 ring-accent/40 ring-inset'
                : 'text-ink-muted ring-1 ring-white/15 ring-inset hover:bg-hover hover:text-ink'
            }`}
            style={
              each === figure || each === 'amount' ? undefined : { color: NUTRIENT_COLORS[each] }
            }
          >
            {each === 'amount' ? 'Amount' : NUTRIENT_LABELS[each]}
          </button>
        ))}
      </div>

      {/* The units, one chip apiece — a filter row's shape, one on at a time. */}
      <div role="radiogroup" aria-label="Unit" className="flex flex-wrap gap-2">
        {units.map((unit, at) => (
          <Fragment key={unit.key}>
            {/*
             * A line where the kind changes: the volumes, then the weights, then the portions.
             * Three kinds of thing in one row read as one long row without it.
             */}
            {at > 0 && units[at - 1]?.group !== unit.group && (
              <span aria-hidden className="my-1 w-px self-stretch bg-white/15" />
            )}
            <button
              type="button"
              role="radio"
              aria-checked={unit.key === chosenKey}
              onClick={() => {
                setTyped(null)
                onAmount(unit.amount)
              }}
              className={`flex h-8 items-center rounded-full px-3.5 text-sm transition-colors ${
                unit.key === chosenKey
                  ? 'bg-accent/15 text-accent ring-1 ring-accent/40 ring-inset'
                  : 'text-ink-muted ring-1 ring-white/15 ring-inset hover:bg-hover hover:text-ink'
              }`}
            >
              {unit.label}
            </button>
          </Fragment>
        ))}
      </div>

      {unsolvable && (
        <p className="text-sm text-danger">
          This food holds no {NUTRIENT_LABELS[figure].toLowerCase()}, so no amount of it comes to
          that.
        </p>
      )}

      {/* What it comes to, drawn as it is typed — on the dials a food is read on everywhere. */}
      <FoodDials
        comesTo={unsolvable ? null : comesTo}
        dayTotalWithout={dayTotal}
        goal={goal}
        label="This comes to"
      />
      {figure !== 'amount' && !unsolvable && comesTo !== null && (
        <p aria-live="polite" className="text-sm text-ink-muted">
          That is {amount.value === 0 ? '—' : `${String(Math.round(amount.value * 100) / 100)} `}
          {amount.unit === 'portion' ? (amount.portion ?? 'portion') : amount.unit}
        </p>
      )}
    </div>
  )
}
